import { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@rtkwlf/pro-solid-svg-icons/faClose';
import { faRotate } from '@rtkwlf/pro-solid-svg-icons/faRotate';
import type { AssetRescanInput } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';

import { useActionDrawer } from '@packages/components/action-drawer/action-drawer-context';
import type { GridRowSelectionModel } from '@packages/fenrir/components';
import { Button, Stack, Typography } from '@packages/fenrir/components';

import { SourceSelector } from '../../source-selector/source-selector';

type RescanAssetsProps = {
  drawerData: AssetRescanInput[];
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
};

export const RescanAssets: React.FC<RescanAssetsProps> = ({
  drawerData,
  setRowSelectionModel,
}) => {
  const { closeDrawer } = useActionDrawer();
  const { t: tRescanCommon } = useTranslation('ads', {
    keyPrefix: 'assetRescanCommon',
  });

  const { t: tUi } = useTranslation('ui');

  const [selectedSource, setSelectedSource] = useState<string>('all');

  const onSubmit = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const submissionData =
      selectedSource === 'all'
        ? drawerData
        : drawerData.map((item) => {
            return {
              ...item,
              sources: item?.sources?.filter((src) => src === selectedSource),
            };
          });
    closeDrawer();
    setRowSelectionModel([]);
  };

  const hasMultipleSources = useMemo(() => {
    return drawerData.some((item) => item.sources && item?.sources?.length > 1);
  }, [drawerData]);

  return (
    <Stack sx={{ height: '100%', p: 4 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={4}
      >
        <Typography variant="h3">
          {tRescanCommon('actionDrawer.title')}
        </Typography>
        <Button
          startIcon={<FontAwesomeIcon icon={faClose} />}
          onClick={closeDrawer}
        >
          {tUi('close')}
        </Button>
      </Stack>
      <Typography>
        {tRescanCommon('actionDrawer.assetsSelected', {
          count: drawerData.length,
        })}
      </Typography>

      {hasMultipleSources && (
        <SourceSelector setSelectedSource={setSelectedSource} />
      )}

      <Typography my={2}>
        {tRescanCommon('actionDrawer.warningStatement')}
      </Typography>

      <Stack
        spacing={2}
        direction="row"
        sx={{
          wdith: '100%',
          height: '100%',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={closeDrawer}>{tUi('button.cancel')}</Button>

        <Button
          color="primary"
          variant="contained"
          type="submit"
          startIcon={
            <FontAwesomeIcon
              icon={faRotate}
              color="var(--aw-palette-common-white)"
            />
          }
          onClick={onSubmit}
        >
          {tRescanCommon('actionDrawer.rescanAssetsButton')}
        </Button>
      </Stack>
    </Stack>
  );
};
